import type { PropsWithChildren } from 'react'
import { createContext, useContext, useState, useMemo } from 'react'

import { type Err } from './CreateGroup/useCreateCampaignGroup'
import { CreateGroupModal } from './CreateGroup/CreateGroupModal'
import { CreateGroupSuccessModal } from './CreateGroup/SuccessModal'
import { CreateGroupFailureModal } from './CreateGroup/FailureModal'
import { EditGroupModal } from './EditGroup/EditGroupModal'
import { EditGroupSuccessModal } from './EditGroup/SuccessModal'
import { EditGroupFailureModal } from './EditGroup/FailureModal'
import { ArchiveGroupModal } from './ArchiveGroup/ArchiveGroupModal'
import { ArchiveGroupSuccessModal } from './ArchiveGroup/SuccessModal'
import { ArchiveGroupFailureModal } from './ArchiveGroup/FailureModal'

type GroupRef = { id: string; name: string }

export type GroupActionsDialogsContextType = Readonly<{
  createGroup: () => void
  editGroup: (group: GroupRef) => void
  archiveGroup: (group: GroupRef) => void
}>

const GroupActionsDialogsContext = createContext<GroupActionsDialogsContextType | null>(null)

export type GroupActionsDialogsProviderProps = Readonly<{
  refresh: () => void
}>

const emptyGroup: GroupRef = { id: '', name: '' }

export function GroupActionsDialogsProvider({
  children,
  refresh
}: PropsWithChildren<GroupActionsDialogsProviderProps>) {
  const [isCreatingGroup, setIsCreatingGroup] = useState(false)
  const [createGroupSuccess, setCreateGroupSuccess] = useState(false)
  const [createGroupError, setCreateGroupError] = useState<Err | null>(null)

  const [editingGroup, setEditingGroup] = useState<GroupRef | null>(null)
  const [editGroupSuccess, setEditGroupSuccess] = useState(false)
  const [editGroupError, setEditGroupError] = useState<Err | null>(null)

  const [archivingGroup, setArchivingGroup] = useState<GroupRef | null>(null)
  const [archiveGroupSuccess, setArchiveGroupSuccess] = useState(false)
  const [archiveGroupError, setArchiveGroupError] = useState<Err | null>(null)

  const onCreateGroupSuccess = () => {
    setIsCreatingGroup(false)
    setCreateGroupSuccess(true)
    refresh()
  }

  const onCreateGroupError = (error?: Err) => {
    setIsCreatingGroup(false)
    setCreateGroupError(error ?? null)
  }

  const onEditGroupSuccess = () => {
    setEditingGroup(null)
    setEditGroupSuccess(true)
    refresh()
  }

  const onEditGroupError = (error?: Err) => {
    setEditingGroup(null)
    setEditGroupError(error ?? null)
  }

  const onArchiveGroupSuccess = () => {
    setArchivingGroup(null)
    setArchiveGroupSuccess(true)
    refresh()
  }

  const onArchiveGroupError = (error?: Err) => {
    setArchivingGroup(null)
    setArchiveGroupError(error ?? null)
  }

  const value = useMemo(
    () => ({
      createGroup: () => setIsCreatingGroup(true),
      editGroup: (group: GroupRef) => setEditingGroup(group),
      archiveGroup: (group: GroupRef) => setArchivingGroup(group)
    }),
    []
  )

  return (
    <GroupActionsDialogsContext.Provider value={value}>
      {children}
      <CreateGroupModal
        isOpen={isCreatingGroup}
        handleClose={() => setIsCreatingGroup(false)}
        onSuccess={onCreateGroupSuccess}
        onError={onCreateGroupError}
      />
      <CreateGroupSuccessModal isOpen={createGroupSuccess} handleClose={() => setCreateGroupSuccess(false)} />
      <CreateGroupFailureModal isOpen={!!createGroupError} handleClose={() => setCreateGroupError(null)} />

      <EditGroupModal
        isOpen={!!editingGroup}
        handleClose={() => setEditingGroup(null)}
        group={editingGroup ?? emptyGroup}
        onSuccess={onEditGroupSuccess}
        onError={onEditGroupError}
      />
      <EditGroupSuccessModal isOpen={editGroupSuccess} handleClose={() => setEditGroupSuccess(false)} />
      <EditGroupFailureModal isOpen={!!editGroupError} handleClose={() => setEditGroupError(null)} />

      <ArchiveGroupModal
        isOpen={!!archivingGroup}
        handleClose={() => setArchivingGroup(null)}
        group={archivingGroup ?? emptyGroup}
        onSuccess={onArchiveGroupSuccess}
        onError={onArchiveGroupError}
      />
      <ArchiveGroupSuccessModal isOpen={archiveGroupSuccess} handleClose={() => setArchiveGroupSuccess(false)} />
      <ArchiveGroupFailureModal isOpen={!!archiveGroupError} handleClose={() => setArchiveGroupError(null)} />
    </GroupActionsDialogsContext.Provider>
  )
}

export const useGroupActionsDialogs = () => {
  const context = useContext(GroupActionsDialogsContext)

  if (!context) {
    throw new Error('useGroupActionsDialogs must be used within a GroupActionsDialogsProvider')
  }

  return context
}
